import React from 'react'
import classnames from 'classnames'
import { t } from 'src/translations'
import { DoneIcon } from '@toasttab/buffet-pui-icons'

export type ModifierGroupWrapperProps = {
  guid: string
  name?: string | null
  ariaLabel: string
  required: boolean
  minSelections?: number | null
  maxSelections?: number | null
  /** Optional - only needed for modifier groups, not variation option sets */
  isModifierInvalid?: (guid: string) => boolean
  isModifierValid?: (guid: string) => boolean
  registerModifierGroup?: (guid: string) => {
    [key: string]: React.RefObject<HTMLDivElement>
  }
  isNested?: boolean
  isInvalid?: boolean
  children: React.ReactNode
}

export const ModifierGroupWrapper = ({
  guid,
  name,
  ariaLabel,
  required,
  minSelections,
  maxSelections,
  isModifierInvalid,
  isModifierValid,
  registerModifierGroup,
  isNested = false,
  isInvalid = false,
  children
}: ModifierGroupWrapperProps) => {
  // Support both direct isInvalid prop and function-based isModifierInvalid
  const showInvalidHighlight = isInvalid || isModifierInvalid?.(guid)
  const showValidState = isModifierValid?.(guid)

  return (
    <div
      className={classnames('modifierGroupRow', {
        nestedModifierGroup: isNested
      })}
      {...(registerModifierGroup ? registerModifierGroup(guid) : {})}
      data-testid={`modifierGroup-${guid}`}
    >
      <div className='modifierGroupHeader'>
        <label id={ariaLabel} htmlFor={isNested ? undefined : guid}>
          <h3 className='modSectionTitle'>{name}</h3>
        </label>
        {required && (
          <div
            className={classnames('required', {
              highlight: showInvalidHighlight,
              valid: showValidState
            })}
          >
            {showValidState && <DoneIcon accessibility='decorative' />}
            {t('modifiers-section.required')}
          </div>
        )}
      </div>

      <div className='modSectionSubtitle'>
        {maxSelections &&
          (minSelections === maxSelections
            ? t('modifiers-section.please-select-exact', {
                count: maxSelections
              })
            : minSelections
            ? t('modifiers-section.please-select-range', {
                min: minSelections,
                max: maxSelections
              })
            : t('modifiers-section.please-select-up-to', {
                count: maxSelections
              }))}
      </div>

      {children}
    </div>
  )
}
